import { testTitle } from '@boViews/shared/testDisplay';
import TestStatusBadge from '@boViews/shared/TestStatusBadge';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Typography from '@sharedComponents/Typography';
import { REFERENCE_LANGUAGE } from '@src/i18n';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CollectionsSection from './CollectionsSection';
import InformationSection from './InformationSection';
import { toFormValues, toPayload } from './testForm';

/**
 * Page of an existing test: header with its title and status, "Information" section and the collections that
 * hold the test (`test.collections`). `languages` are the catalogue languages (their id labelled by their code) and
 * `levels` the levels of the tests. `onSave(payload)` resolves to the saved test, or to null when the save failed;
 * `onOpenCollection(collection)` opens a collection; `onDirtyChange` reports whether the form holds unsaved changes.
 */
const TestDetail = ({
    test,
    languages = [],
    levels = [],
    onSave = async () => null,
    onOpenCollection = () => {},
    onBack = () => {},
    onDirtyChange = () => {},
} = {}) => {
    const { t } = useTranslation('bo');
    const methods = useForm({ defaultValues: toFormValues(test, languages) });
    const {
        getValues,
        setError,
        reset,
        formState: { isDirty },
    } = methods;
    const [activeLanguage, setActiveLanguage] = useState(REFERENCE_LANGUAGE);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    const save = async () => {
        const values = getValues();
        if (!(values.title?.[REFERENCE_LANGUAGE] ?? '').trim()) {
            setActiveLanguage(REFERENCE_LANGUAGE);
            setError(`title.${REFERENCE_LANGUAGE}`, { type: 'required', message: t('The French title is required') });
            return;
        }
        setSaving(true);
        const saved = await onSave(toPayload(values)).catch(() => null);
        setSaving(false);
        // keepFieldsRef: the inputs must stay registered, as their memoized ref callbacks do not run again after a reset.
        if (saved) reset(toFormValues(saved, languages), { keepFieldsRef: true });
    };

    return (
        <FlexBox flexDirection="column" gap="m" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <Button ghost noPadding icon={faArrowLeft} label={t('Back to tests')} action={onBack} style={{ alignSelf: 'flex-start' }} />

            <FlexBox gap="s" alignItems="center">
                <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                    {testTitle(test) || test.reference}
                </Typography>
                <TestStatusBadge status={test.status} />
            </FlexBox>

            <FormProvider {...methods}>
                <InformationSection
                    reference={test.reference}
                    languages={languages}
                    levels={levels}
                    activeLanguage={activeLanguage}
                    saving={saving}
                    onLanguageChange={setActiveLanguage}
                    onSave={save}
                />
            </FormProvider>

            <CollectionsSection collections={test.collections ?? []} onOpen={onOpenCollection} />
        </FlexBox>
    );
};

export default TestDetail;
